/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { IInspectorControl_BuilderAny, TInspectorControlProps } from "../InspectorControlTypes";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { SCHEMA_BUILDER_ANY_VALUE_TYPE } from "@adapptio/lib-blueprint";
import { FieldGroup, FieldSelect, TSelectOption } from "@adapptio/lib-editor-ui";

export interface IInspectorControlBuilderAnyProps
	extends TInspectorControlProps<IInspectorControl_BuilderAny> {}

export const InspectorControlBuilderAny: React.FC<IInspectorControlBuilderAnyProps> = ({ model }) => {
	const editorInspectorCtx = useEditorInspectorContext();

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setType(model, value as SCHEMA_BUILDER_ANY_VALUE_TYPE, undefined, true);
		},
		[ model ]
	);

	const options = useMemo(() => {
		const items: TSelectOption[] = [
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.STRING,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.STRING,
				label: "String",
				description: "Text data.",
				icon: "mdi/text-short"
			},
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.PASSWORD,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.PASSWORD,
				label: "Password",
				description: "Password value that will be encrypted",
				icon: "mdi/lock"
			},
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.INTEGER,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.INTEGER,
				label: "Integer",
				description: "Number without decimals",
				icon: "mdi/numeric"
			},
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.FLOAT,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.FLOAT,
				label: "Float",
				description: "Number with decimal places",
				icon: "mdi/numeric"
			},
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.BOOLEAN,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.BOOLEAN,
				label: "Boolean",
				description: "True or false",
				icon: "mdi/checkbox-marked-outline"
			},
			// @todo Enable when date will be implemented
			// {
			// 	value: SCHEMA_BUILDER_ANY_VALUE_TYPE.DATE,
			// 	label: "Date",
			// 	icon: "mdi/calendar-clock"
			// },
			{
				isSeparator: true
			},
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.ENUM_STRING,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.ENUM_STRING,
				label: "Enum: String",
				description: "Select from text values",
				icon: "mdi/format-list-bulleted"
			},
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.ENUM_INTEGER,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.ENUM_INTEGER,
				label: "Enum: Integer",
				description: "Select from numberic values without decimals",
				icon: "mdi/format-list-bulleted"
			},
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.ENUM_FLOAT,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.ENUM_FLOAT,
				label: "Enum: Float",
				description: "Select from numeric values with decimals",
				icon: "mdi/format-list-bulleted"
			},
			{
				isSeparator: true
			},
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.OBJECT,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.OBJECT,
				label: "Object",
				description: "Key-value data with fixed properties.",
				icon: "mdi/format-list-bulleted-type"
			},
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.ARRAY,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.ARRAY,
				label: "Array",
				description: "List of values",
				icon: "mdi/code-brackets"
			},
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.MAP,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.MAP,
				label: "Map",
				description: "Key-value pairs with dynamic text keys",
				icon: "mdi/code-braces"
			},
			{
				isSeparator: true
			},
			{
				id: SCHEMA_BUILDER_ANY_VALUE_TYPE.ANY,
				value: SCHEMA_BUILDER_ANY_VALUE_TYPE.ANY,
				label: "Any",
				description: "Any type",
				icon: "mdi/help-rhombus"
			}
		];

		return items;
	}, []);

	return (
		<FieldGroup grow preventTruncate>
			<FieldSelect
				value={model.type}
				options={options}
				readOnly={editorInspectorCtx?.readOnly}
				onChange={handleChange}
			/>
		</FieldGroup>
	);
};
